import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe, DecimalPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { ConversionService, StateService } from '@cosmos/angular/utils';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActionDefinition, ColumnDefinition } from './column-definition.model';
import { resolveColumnValue } from './column-value';
import { PaginatorModule } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { MeasurementLivenessBadgeComponent } from '../../components/measurement-liveness-badge/measurement-liveness-badge.component';

@Component({
  selector: 'cosmos-data-table',
  imports: [
    NgClass,
    NgTemplateOutlet,
    TranslatePipe,
    TableModule,
    CardModule,
    ButtonModule,
    PaginatorModule,
    ProgressSpinnerModule,
    TagModule,
    MeasurementLivenessBadgeComponent,
  ],
  templateUrl: './data-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataTableComponent {
  private readonly router = inject(Router);
  private readonly currentRoute = inject(ActivatedRoute);
  private readonly conversionService = inject(ConversionService);
  private readonly datePipe = inject(DatePipe);
  private readonly decimalPipe = inject(DecimalPipe);
  private readonly stateService = inject(StateService);
  private readonly translateService = inject(TranslateService);

  // ── Inputs ────────────────────────────────────────────────────────────────
  readonly tableData    = input<any[]>([]);
  readonly tableColumns = input<ColumnDefinition[]>([]);
  readonly rowActions   = input<ActionDefinition[]>([]);
  readonly isLinked     = input<string | boolean>(false);
  readonly isPageable   = input(false);
  readonly isScrollable = input(false); // opt-in only — avoids header/body width sync lag on sort
  readonly wrapper     = input(true);  // set false to render table without the p-card shell
  readonly paginationSizes  = input<number[]>([10, 25, 50]);
  readonly defaultPageSize  = input<number>(10);
  readonly paginatorFirst   = input<number>(0);
  readonly paginatorCount   = input<number | undefined>(undefined);
  readonly sortField = input<string>('');
  readonly sortOrder = input<1 | -1>(1);

  // ── Outputs ───────────────────────────────────────────────────────────────
  readonly sortChange = output<{ field: string; order: 1 | -1 }>();
  readonly rowAction  = output<any>();
  readonly pageChange = output<any>();
  readonly rowClick   = output<any>();

  // ── Internal sort/page state (signals) ───────────────────────────────────
  // These are the ONLY mutable signals. They are never written from inside an
  // effect or computed, only from user events — so there is no reactive cycle.
  private readonly localSortField  = signal<string | null>(null);
  private readonly localSortOrder  = signal<1 | -1>(1);
  readonly currentFirst            = signal<number>(0);
  readonly currentPageSize         = signal<number>(10);

  // ── Derived display state (computed) ─────────────────────────────────────

  readonly displayedColumns = computed(() =>
    this.tableColumns().filter(col => col.visible)
  );

  readonly displayedActions = computed(() =>
    (this.rowActions() ?? []).filter(action => {
      const roles = this.stateService.getCurrentUser()?.roles ?? [];
      return action.role == undefined || roles.includes(action.role);
    })
  );

  // The full dataset sorted — recalculated only when data or sort state changes.
  private readonly sortedData = computed(() => {
    const data  = this.tableData();
    const field = this.localSortField();
    const order = this.localSortOrder();
    if (!field) return data;
    const parts = field.split('.');
    return [...data].sort((a, b) => {
      const aVal = parts.reduce((acc: any, key) => acc?.[key], a);
      const bVal = parts.reduce((acc: any, key) => acc?.[key], b);
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal) * order;
      }
      return (aVal < bVal ? -1 : aVal > bVal ? 1 : 0) * order;
    });
  });

  // The page slice shown in the table — recalculated only when sorted data or
  // page state changes. For server-managed mode the caller provides the page.
  readonly tableDataSource = computed(() => {
    if (this.paginatorCount() != undefined) return this.tableData();
    const first    = this.currentFirst();
    const pageSize = this.currentPageSize();
    return this.sortedData().slice(first, first + pageSize);
  });

  // Total records for the paginator.
  readonly totalRecords = computed(() =>
    this.paginatorCount() ?? this.sortedData().length
  );

  // Reset to page 1 whenever the underlying data changes in local mode.
  // This is an effect that only WRITES to localSortField-unrelated signals, and
  // it reads tableData() which never changes as a result of its own write —
  // no cycle is possible.
  constructor() {
    this.currentPageSize.set(this.defaultPageSize());
    this.currentFirst.set(this.paginatorFirst());

    effect(() => {
      // Reading tableData() registers it as the sole dependency.
      // On new data, reset to page 1 (local mode only).
      this.tableData();
      if (this.paginatorCount() == undefined) {
        this.currentFirst.set(0);
      }
    });

    // Keep currentFirst in sync with the paginatorFirst input so that
    // parent-driven resets (search, sort) are reflected in the paginator UI.
    effect(() => {
      this.currentFirst.set(this.paginatorFirst());
    });

    // Sync defaultPageSize input after Angular resolves parent bindings.
    // The constructor reads it too early (before the parent value is bound),
    // so it always sees the input's fallback default of 10.
    effect(() => {
      this.currentPageSize.set(this.defaultPageSize());
    });
  }

  // ── Event handlers ────────────────────────────────────────────────────────

  // PrimeNG calls this when [customSort]="true" — it passes the event but does NOT
  // sort [value] itself, so there is no array mutation → no reactive loop.
  onSort($event: { field: string; order: number; data: any[] }): void {
    if (this.paginatorCount() != undefined) {
      // Server-managed: emit so host re-fetches with new sort params.
      if ($event.field === this.sortField() && $event.order === this.sortOrder()) return;
      this.sortChange.emit({ field: $event.field, order: $event.order as 1 | -1 });
    } else {
      // Local mode: update sort signals — sortedData computed recomputes,
      // tableDataSource computed recomputes, template re-renders. No loop.
      this.localSortField.set($event.field);
      this.localSortOrder.set($event.order as 1 | -1);
      this.currentFirst.set(0);
    }
  }

  onPageChange($event: any): void {
    this.currentPageSize.set($event.rows);
    this.currentFirst.set($event.first);
    if (this.paginatorCount() != undefined) {
      this.pageChange.emit($event);
    }
    // Local mode: tableDataSource computed re-runs automatically.
  }

  emitRowAction(action: string, row: any): void {
    this.rowAction.emit({ action, row });
  }

  updateColumns(i?: number, $event?: any): void {
    // tableColumns() is a readonly input signal — mutate the element in place
    // so displayedColumns computed picks up the change on next read.
    if (i != null) this.tableColumns()[i].visible = $event.checked;
  }

  onRowClick(row: any): void {
    if (this.isLinked() !== true) this.route(row)
    else if (this.isLinked() === true) this.rowClick.emit(row);
  }

  // ── Column value resolution ───────────────────────────────────────────────

  findColumnValue(element: unknown, column: ColumnDefinition): string {
    return resolveColumnValue(element, column, {
      datePipe: this.datePipe,
      decimalPipe: this.decimalPipe,
      translate: this.translateService,
      conversion: this.conversionService,
    });
  }

  getTagSeverity(element: any, column: ColumnDefinition): "success" | "secondary" | "info" | "warn" | "danger" | "contrast" | null | undefined {
    const value = element[column.name];
    return column.tagColorMap?.[value] ?? 'secondary';
  }

  getTagLabel(element: any, column: ColumnDefinition): string {
    const raw = this.findColumnValue(element, column);
    if (column.tagTranslatePrefix) {
      return this.translateService.instant(column.tagTranslatePrefix + raw);
    }
    return raw;
  }

  /** For type='liveness': resolves the row's last-seen timestamp from its `time` array. */
  getLivenessLastTimestamp(element: any): Date | null {
    const time = element?.time;
    return time?.length ? time[time.length - 1] : null;
  }

  getIconColor(element: any, column: ColumnDefinition): string {
    const value = this.findColumnValue(element, column);
    switch (value) {
      case 'info':    return 'LimeGreen';
      case 'warning': return 'Gold';
      case 'error':   return 'OrangeRed';
      default:        return '';
    }
  }

  getIcon(element: any, column: ColumnDefinition): string {
    const value = this.findColumnValue(element, column);
    switch (value) {
      case 'info':    return 'fa-solid fa-circle-info fa-xl';
      case 'warning': return 'fa-solid fa-circle-exclamation fa-xl';
      case 'error':   return 'fa-solid fa-circle-xmark fa-xl';
      default:        return '';
    }
  }

  route(row: any): void {
    switch (this.isLinked()) {
      case 'active_measurements':
        this.router.navigate(['/projects/' + row.project._id + '/monitoring/measurement/' + row._id]); break;
      case 'project':
        this.router.navigate(['/projects/' + row._id]); break;
      case 'device':
        this.router.navigate(['/devices/d/' + row._id]); break;
      case 'gateway':
        this.router.navigate(['/devices/g/' + row._id]); break;
      case 'stage':
        this.router.navigate(['/projects/' + row.project + '/monitoring/stage/' + row._id]); break;
      case 'stage-only':
        this.router.navigate(['/stages/' + row._id]); break;
      case 'measurement':
        this.router.navigate(['/projects/' + row.project + '/monitoring/measurement/' + row._id]); break;
      case 'measurement-only':
        this.router.navigate(['/measurements/' + row._id]); break;
      case 'simulations':
        this.router.navigate(['/simulations/' + row._id]); break;
      case 'document-templates':
        this.router.navigate(['/document-templates/' + row._id]); break;
      case 'processing-job':
        this.router.navigate([row._id], { relativeTo: this.currentRoute }); break;
      case 'test-result':
        this.router.navigate([row._id], { relativeTo: this.currentRoute }); break;
      case 'concrete-mixture':
        this.router.navigate(['/projects/' + row.project + '/concretes/' + row._id]); break;
    }
  }
}
